import { FlightType, SegmentType } from "src/Modules/Flights/constants";
import { PointItem } from "../constants";
import { toDayjs } from "src/utility";
import { explode } from "src/utility/objects";
import { MiniRule } from "src/Modules/Flights/constants/JourneyDetails";
import { MappedJourney, Policy } from "src/Modules/Flights/constants/JourneyListing";
import { ReactNode } from "react";
import i18n from "src/i18n";
import { formatDate, formatTimeFromUnit } from "src/utility/time";

function transformFlightsToTimeline(flights: FlightType[]): PointItem[] {
  if (!flights.length) return [];

  const firstFlight = flights[0];
  const lastFlight = flights[flights.length - 1];
  const dayOffset = toDayjs(lastFlight.arrival).startOf("day").diff(toDayjs(firstFlight.departure).startOf("day"), "day");
  const startPoint = {
    type: "point",
    time: toDayjs(firstFlight.departure),
    position: firstFlight.origin,
    positionType: "start",
  } as PointItem;

  const endPoint = {
    type: "point",
    time: toDayjs(lastFlight.arrival),
    position: lastFlight.destination,
    positionType: "end",
    dayOffset,
  } as PointItem;

  if (flights.length === 1) {
    return [startPoint, endPoint];
  }


  return [startPoint, endPoint];
}

const layoverLabel = (layover: SegmentType["layovers"][number]) => (
  <span key={layover.airport}>
    <span className="text-secondary-60">{layover.city}</span> {i18n.t("Flights.listing.layoverFor", { duration: formatTimeFromUnit(layover.durationMinutes, "m") })}
  </span>
);

function buildJourneySummary(segment: SegmentType): { timeline: PointItem[]; stopLabel: ReactNode, totalDuration:number } {
  const { flights, layovers } = explode(segment, {});
  const timeline = transformFlightsToTimeline(flights ?? []);
  const stops = layovers.length;
  const totalDuration = segment.durationMinutes;

  const stopLabel =
    stops === 0 ? (
      <span>{i18n.t("Flights.listing.direct")}</span>
    ) : stops === 1 ? (
      <span>
        {i18n.t("Flights.listing.oneStopAt")} {layoverLabel(layovers[0])}
      </span>
    ) : (
      <span>
        {i18n.t("Flights.listing.multipleStopsAt")}{" "}
        {layovers.map((layover, index) => (
          <span key={layover.airport}>
            {index > 0 && (index === layovers.length - 1 ? ` ${i18n.t("Flights.listing.and")} ` : ", ")}
            {layoverLabel(layover)}
          </span>
        ))}
      </span>
    );
  return { timeline, stopLabel, totalDuration };
}

const timelineToItem = (point: PointItem) => {
    return (
      <div className="font-inter container flex flex-col" style={{ alignItems: point.positionType }}>
        <span className="text-[clamp(.5rem,3.5cqw,1.1rem)]! relative whitespace-nowrap font-medium text-font-default">
          {formatDate(point.time, "hh:mm A")}
          {!!point.dayOffset && (
            <sup className="absolute -end-4 -top-1 text-[clamp(8px,2cqw,0.65rem)] font-bold leading-none text-icon-secondary">
              +{point.dayOffset}
            </sup>
          )}
        </span>
        <span className="text-font-subtle text-[clamp(10px,2.5cqw,1.2rem)]! whitespace-nowrap font-normal">
          {point.position.cityName}, ({point.position.code})
        </span>
      </div>
    );
};

const formulateRule = (rule: any) => {
  if (!rule) return "";

  const formatTime = (value?: any) => (value != null && value != "" ? `${value} ${rule.unit ?? ""}`.trim() : null);

  const from = formatTime(rule.from);
  const to = formatTime(rule.to);
  const feeText = rule.details != null && rule.details != "Nil" ? `${rule.details}` : null;

  const status = feeText
    ? feeText === "REFER TO DETAILED FARE RULES"
      ? i18n.t("Flights.listing.refundableCheckDetails")
      : i18n.t("Flights.listing.refundableWithFee", { fee: feeText })
    : from || to || rule.details === "Nil"
      ? i18n.t("Flights.listing.freeOfCharge")
      : null;

  const condition =
    from && to
      ? i18n.t("Flights.listing.ruleConditionBetween", { from, to })
      : from
        ? i18n.t("Flights.listing.ruleConditionUpTo", { from })
        : to
          ? i18n.t("Flights.listing.ruleConditionValidUntil", { to })
          : "";
  const sentence = `${status} ${condition}`.trim();
  return `${sentence}.`;
};

function mapMiniRules(rules: MiniRule[]): MappedJourney[] {
  const flatRules = rules.flat();
  if (flatRules.length === 0) return [];
  const journeyMap = new Map<string, Map<string, Policy>>();

  for (const rule of flatRules) {
    if (!journeyMap.has(rule.journeyPoints)) {
      journeyMap.set(rule.journeyPoints, new Map());
    }
    const policyMap = journeyMap.get(rule.journeyPoints)!;
    const typeKey = rule.type.toLowerCase();
    if (!policyMap.has(typeKey)) {
      policyMap.set(typeKey, { type: typeKey, rules: [] });
    }
    policyMap.get(typeKey)!.rules.push({
      from: rule.from,
      to: rule.to,
      unit: rule.unit,
      details: rule.details,
    });
  }

  return Array.from(journeyMap.entries()).map(([key, policyMap]) => ({
    key,
    policies: Array.from(policyMap.values()),
  }));
}

export { timelineToItem, buildJourneySummary, formulateRule, mapMiniRules };
